import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useSelector } from "react-redux";
import useFirestoreCollection from "../hooks/useFirestoreCollection";
import { activeClientSelectors } from "../redux/client-case/selectors";
import { ExhibitListFormat } from "../types/exhibit_formats";
import { DATABASE } from "../types/tables-data";

type ListFormatContextProps = {
  loading: boolean;
  exhibitListFormat: ExhibitListFormat | undefined;
};

export const ListFormatContext = createContext<ListFormatContextProps | null>(
  null
);

type ListFormatProviderProps = {
  children: ReactNode;
  /**
   * Overrides the case's active exhibit list format id from Redux. Used by
   * flows that operate on a specific format rather than the case default.
   */
  formatIdOverride?: string;
};

export const ListFormatProvider = ({
  children,
  formatIdOverride
}: ListFormatProviderProps) => {
  const reduxExhibitListFormatId = useSelector(
    activeClientSelectors.selectExhibitListFormat
  );
  const exhibitListFormatId = formatIdOverride ?? reduxExhibitListFormatId;

  const { documents: formats, loading } =
    useFirestoreCollection<ExhibitListFormat>(DATABASE.EXHIBIT_LIST_FORMATS, {
      listen: true,
      useLawyerAccessMap: true,
      enableToast: true
    });

  const exhibitListFormat = useMemo(() => {
    if (!formats) return undefined;
    const selectedFormat = exhibitListFormatId
      ? formats.find((_format) => _format.id === exhibitListFormatId)
      : undefined;
    return (
      selectedFormat ?? formats.find((_format) => _format.id === "default")
    );
  }, [formats, exhibitListFormatId]);

  const contextValue = useMemo(
    () => ({ loading, exhibitListFormat }),
    [loading, exhibitListFormat]
  );

  return (
    <ListFormatContext.Provider value={contextValue}>
      {children}
    </ListFormatContext.Provider>
  );
};

export const useListFormatContext = () => {
  const context = useContext(ListFormatContext);
  if (!context) {
    throw Error("You need to provider a ListFormatProvider to use this hook");
  }
  return context;
};
